import React, { useState, useEffect } from 'react';
import { useAuth } from '../helpers/AuthContext';
import './CustomerMessage.css';

const DISABLED_LINK_PATTERN =
    /<span class="disabled-link" data-url="([^"]+)">([^<]+)<\/span>/gi;

const getSafeLinkUrl = (value) => {
    if (typeof value !== 'string') return null;

    const trimmedValue = value.trim();
    const hasProtocol = /^https?:\/\//i.test(trimmedValue);
    const isDomain = /^(?:www\.)?[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?\.[a-z]{2,}(?:[/?#].*)?$/i
        .test(trimmedValue);
    if (!hasProtocol && !isDomain) return null;

    const normalizedValue = hasProtocol ? trimmedValue : `https://${trimmedValue}`;
    try {
        const parsedUrl = new URL(normalizedValue);
        return ['http:', 'https:'].includes(parsedUrl.protocol) ? normalizedValue : null;
    } catch {
        return null;
    }
};

const renderMessageText = (sanitizedText, linksEnabled) => {
    const text = typeof sanitizedText === 'string' ? sanitizedText : '';
    const parts = [];
    let lastIndex = 0;
    let match;

    DISABLED_LINK_PATTERN.lastIndex = 0;
    while ((match = DISABLED_LINK_PATTERN.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
        }

        const safeUrl = getSafeLinkUrl(match[1]);
        const key = `${match.index}-${match[1]}`;
        if (linksEnabled && safeUrl) {
            parts.push(
                <a key={key} href={safeUrl} target="_blank" rel="noopener noreferrer">
                    {match[2]}
                </a>
            );
        } else {
            parts.push(
                <span key={key} className="disabled-link">
                    {match[2]}
                </span>
            );
        }
        lastIndex = DISABLED_LINK_PATTERN.lastIndex;
    }

    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }

    return parts;
};

const hasEnableableLink = (sanitizedText) => {
    if (typeof sanitizedText !== 'string') return false;

    DISABLED_LINK_PATTERN.lastIndex = 0;
    let match;
    while ((match = DISABLED_LINK_PATTERN.exec(sanitizedText)) !== null) {
        if (getSafeLinkUrl(match[1])) return true;
    }
    return false;
};

// Presentational component
function CustomerMessage({ message, loading, error, isRevealed, linksEnabled, onReveal, onEnableLinks, onReport, reportUrl, debug=false }) {
    if (loading) {
        return <div className="message-loading">Loading message...</div>;
    }
    if (error) {
        return <div className="message-error">Error loading message: {error}</div>;
    }
    if (!message) {
        return <div className="message-error">Message not found</div>;
    }
    const className = message.inbox ? "inbox" : "outbox";
    const senderName = message.inbox ? message.PhoneNumber.nickname : "You";
    const messageDate = new Date(message.createdAt).toLocaleString();
    const displayText = renderMessageText(message.sanitizedText, linksEnabled);
    const canEnableLinks = hasEnableableLink(message.sanitizedText);
    const spamDetector = message.spamDetector || {};
    const isSpam = !!(message.isSpam || spamDetector.isSpam);
    const isMarkedSpam = !!(message.isModerated && message.isSpam); // was human-marked as spam
    const hasLinks = message.containsUrls || (spamDetector.metadata?.urlCount || 0) > 0;

    const MessageImages = Array.isArray(message.media) && message.media.length > 0 ? (
        <div className="message-images">
            {message.media.map((m, idx) => {
                const url = typeof m === 'string' ? m : (m?.cdnPath || m?.url);
                if (!url) return null;
                const key = (typeof m === 'object' && m?.uuid) ? m.uuid : `${idx}-${url}`;
                return (
                    <a key={key} href={url} target="_blank" rel="noopener noreferrer">
                        <img src={url} alt="Attachment" style={{ maxWidth: 200, maxHeight: 200, margin: '4px 4px 0 0', display: 'inline-block', borderRadius: 4 }} />
                    </a>
                );
            })}
        </div>
    ) : null;

    const DebugInfo = debug ? (
        <div className="debug-info">
            <pre>
                {JSON.stringify(message.spamDetector, null, 2)}
            </pre>
        </div>
    ) : null;

    const PotentialSpamWarning = (
        <li className={className}>
            <div className="potential-spam-message" data-message-id={message.id}>
                <div className="spam-warning">
                    <strong>⚠️ Potential Spam or Scam</strong>
                    <p>This message has been hidden for your security. Only reveal it if you trust the sender.</p>
                    <button
                        type="button"
                        className="reveal-message-btn"
                        onClick={onReveal}
                    >
                        Reveal Message
                    </button>
                    <p>We will <strong>NEVER</strong> ask you to "verify" your account here.<br/> Do not give any information or money away because of messages you see here.</p>
                </div>
            </div>
        </li>
    );

    const MarkedSpamWarning = (
        <li className={className}>
            <div className="potential-spam-message" data-message-id={message.id}>
                <div className="spam-warning">
                    <strong>⚠️ This message was marked as spam</strong>
                    <p>This message has been hidden for your security.</p>
                </div>
            </div>
        </li>
    );

    // If the message is marked as spam, show a warning
    if (isMarkedSpam) {
        return MarkedSpamWarning;
    }

    // Spam warning with reveal button
    if ((isSpam || hasLinks) && message.inbox && !isRevealed) {
        return PotentialSpamWarning;
    }

    // Revealed spam message with links
    if (message.containsUrls && message.inbox && isRevealed) {
        return (
            <li className={className}>
                <div className="potential-spam-message" data-message-id={message.id}>
                    <div className="hidden-message-content">
                        <p className="message-content">
                            <strong>{senderName}</strong>:
                            <span className="message-text-with-disabled-links">
                                {displayText}
                            </span>
                            {!linksEnabled && canEnableLinks && (
                                <button
                                    type="button"
                                    className="enable-links-btn"
                                    onClick={onEnableLinks}
                                    style={{ marginLeft: '10px', fontSize: '0.8em' }}
                                >
                                    Show Links
                                </button>
                            )}
                        </p>
                        {MessageImages}
                        <p className="date">{messageDate}</p>
                        {message.inbox && (
                            <p className="message-actions">
                                {reportUrl ? (
                                    <a className="report-link" href={reportUrl}>Report</a>
                                ) : (
                                    <button className="report-link" onClick={onReport}>Report</button>
                                )}
                            </p>
                        )}
                    </div>
                </div>
            </li>
        );
    }

    // Regular message (no spam, no links)
    return (
        <li className={className}>
            <p className="message-content">
                <strong>{senderName}</strong>: {message.text}
            </p>
            {MessageImages}
            <p className="date">{messageDate}</p>
            {message.inbox && (
                <p className="message-actions">
                    {reportUrl ? (
                        <a className="report-link" href={reportUrl}>Report</a>
                    ) : (
                        <button className="report-link" onClick={onReport}>Report</button>
                    )}
                </p>
            )}
            {debug && DebugInfo}
        </li>
    );
}

// Connected component
function ConnectedCustomerMessage({ messageId, debug=false }) {
    const { fetch } = useAuth();
    const [message, setMessage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isRevealed, setIsRevealed] = useState(false);
    const [linksEnabled, setLinksEnabled] = useState(false);

    useEffect(() => {
        if (!messageId) {
            setError('No message ID provided');
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        fetch(`/account/messages/message/${messageId}?isApi=true`)
            .then(res => {
                if (!res.ok) {
                    throw new Error(`Failed to fetch message: ${res.status}`);
                }
                return res.json();
            })
            .then(data => {
                setMessage(data.message);
                setLoading(false);
            })
            .catch(err => {
                setError(err.message);
                setLoading(false);
            });
    }, [messageId, fetch]);

    const handleRevealMessage = () => setIsRevealed(true);
    const handleEnableLinks = () => setLinksEnabled(true);
    const handleReport = () => {
        if (message) {
            window.location.href = `/account/messages/report?messageId=${message.id}`;
        }
    };

    return (
        <CustomerMessage
            debug={debug}
            message={message}
            loading={loading}
            error={error}
            isRevealed={isRevealed}
            linksEnabled={linksEnabled}
            onReveal={handleRevealMessage}
            onEnableLinks={handleEnableLinks}
            onReport={handleReport}
        />
    );
}

export { CustomerMessage, ConnectedCustomerMessage };
